import { useContext, useState } from "react";
import {
    FailureSeverity,
    NamedProperty,
    createFailure,
    ensure,
    getById,
    useNotifications,
    validationFailed,
    validationSuccessful
} from "sprint-common-web";

import { AirlinesDataContext } from "../../../../../common/data/lookup-data-providers/AirlinesDataProvider";
import { DistributorsDataContext } from "../../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { useAdminWatermarkRuleApi } from "../../api/useAdminWatermarkRuleApi";
import { WatermarkRuleContext } from "../../context/WatermarkRuleContext";
import { WatermarkRuleItem } from "../WatermarkRuleItem";
import { WatermarkRuleFormInputs } from "./WatermarkRuleForm";

export const useWatermarkRuleForm = ({ item, onSubmit }: { item?: WatermarkRuleItem; onSubmit(): void }) => {
    const [isFormLoading, setIsFormLoading] = useState(false);
    const { createWatermarkRule: create, updateWatermarkRule: edit } = useAdminWatermarkRuleApi();
    const { addWatermarkRuleItem, updateWatermarkRuleItem, watermarkRuleItems } = useContext(WatermarkRuleContext);
    const { success, error } = useNotifications();
    const { airlines } = useContext(AirlinesDataContext);
    const { suggestDistributor, getDistributorById } = useContext(DistributorsDataContext);

    const createWatermarkRule = async ({ value, distributorId, position: formPosition }: WatermarkRuleFormInputs) => {
        setIsFormLoading(true);

        const distributor = await getDistributorById(distributorId);
        const position = Number(formPosition);
        create({
            value,
            distributor,
            position
        })
            .then((res) => {
                addWatermarkRuleItem({
                    id: res.entityId,
                    value,
                    distributor,
                    position
                });

                onSubmit();

                success("Watermark rule created succesfully");
            })
            .catch(() => {
                error("Watermark rule creation failed");
            })
            .finally(() => {
                setIsFormLoading(false);
            });
    };

    const updateWatermarkRule = async ({
        ruleId,
        value,
        distributorId,
        position: formPosition
    }: WatermarkRuleFormInputs & { ruleId: string }) => {
        setIsFormLoading(true);
        ensure(item);

        let distributor: NamedProperty;

        if (distributorId === item.distributor.id) {
            const distributors = await suggestDistributor(item.distributor.name);
            distributor = getById(distributors, distributorId);
        } else {
            distributor = await getDistributorById(distributorId);
        }
        const position = Number(formPosition);

        edit({
            id: ruleId,
            value,
            distributor,
            position
        })
            .then(() => {
                updateWatermarkRuleItem({
                    id: ruleId,
                    value,
                    distributor,
                    position
                });

                onSubmit();

                success("Watermark rule updated succesfully");
            })
            .catch(() => {
                error("Watermark rule update failed");
                setIsFormLoading(false);
            })
            .finally(() => {
                setIsFormLoading(false);
            });
    };

    const validateDistributor = (value?: string) => {
        const distributor = watermarkRuleItems.find(
            (w) => w.distributor.id === value && w.distributor.id !== item?.distributor.id
        );

        if (distributor) {
            const failure = createFailure(
                "Watermark already exists with same distributor",
                "sameDistributor",
                FailureSeverity.Error
            );
            return validationFailed([failure]);
        }

        return validationSuccessful();
    };

    return {
        airlines,
        isFormLoading,
        updateWatermarkRule,
        createWatermarkRule,
        validateDistributor
    };
};
